import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, EmptyState } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Phone, Users, Trash2 } from "lucide-react";

export default function Squadre() {
  const [teams, setTeams] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [openC, setOpenC] = useState(false);
  const [openT, setOpenT] = useState(false);
  const [cForm, setCForm] = useState({});
  const [tForm, setTForm] = useState({ member_ids: [] });

  const load = async () => {
    const [t, c] = await Promise.all([api.get("/teams"), api.get("/collaborators")]);
    setTeams(t.data); setCollabs(c.data);
  };
  useEffect(() => { load(); }, []);

  const saveC = async () => { if (!cForm.name) return toast.error("Nome obbligatorio"); await api.post("/collaborators", cForm); toast.success("Collaboratore aggiunto"); setOpenC(false); setCForm({}); load(); };
  const saveT = async () => { if (!tForm.name) return toast.error("Nome obbligatorio"); await api.post("/teams", tForm); toast.success("Squadra creata"); setOpenT(false); setTForm({ member_ids: [] }); load(); };
  const delC = async (id) => { await api.delete(`/collaborators/${id}`); load(); };
  const delT = async (id) => { await api.delete(`/teams/${id}`); load(); };
  const toggleMember = (id) => { const cur = tForm.member_ids || []; setTForm({ ...tForm, member_ids: cur.includes(id) ? cur.filter(x=>x!==id) : [...cur, id] }); };

  return (
    <div>
      <PageHeader title="Squadre e collaboratori" subtitle="Composizione squadre e schede collaboratori">
        <Button variant="outline" data-testid="btn-new-collab" onClick={() => { setCForm({}); setOpenC(true); }}><Plus size={16} className="mr-1" /> Collaboratore</Button>
        <Button className="bg-brand hover:bg-[#A8472F] text-white" data-testid="btn-new-team" onClick={() => { setTForm({ member_ids: [] }); setOpenT(true); }}><Plus size={16} className="mr-1" /> Squadra</Button>
      </PageHeader>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h3 className="font-head font-bold mb-3 flex items-center gap-2"><Users size={18} className="text-brand" /> Squadre</h3>
          {teams.length === 0 ? <EmptyState title="Nessuna squadra" /> : (
            <div className="space-y-3">
              {teams.map((t) => (
                <div key={t.id} data-testid={`team-${t.id}`} className="card-soft p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-bold flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: t.color }} /> {t.name}</p>
                    <button onClick={() => delT(t.id)} className="text-destructive"><Trash2 size={16} /></button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(t.members||[]).map((m) => <span key={m.id} className="text-xs bg-accent px-2 py-1 rounded-full">{m.name}</span>)}
                    {(t.members||[]).length === 0 && <span className="text-xs text-muted-foreground">Nessun componente</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <h3 className="font-head font-bold mb-3">Collaboratori</h3>
          {collabs.length === 0 ? <EmptyState title="Nessun collaboratore" /> : (
            <div className="space-y-2">
              {collabs.map((c) => (
                <div key={c.id} data-testid={`collab-${c.id}`} className="card-soft p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: c.color }}>{c.name[0]}</span>
                    <div><p className="font-medium text-sm">{c.name}</p><p className="text-xs text-muted-foreground">{c.role_note} {c.phone && `· ${c.phone}`}</p></div>
                  </div>
                  <button onClick={() => delC(c.id)} className="text-destructive"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={openC} onOpenChange={setOpenC}>
        <DialogContent data-testid="collab-dialog">
          <DialogHeader><DialogTitle className="font-head">Nuovo collaboratore</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[["name","Nome *"],["phone","Telefono"],["email","Email"],["role_note","Ruolo (es. Caposquadra)"]].map(([k,l])=>(
              <div key={k}><Label>{l}</Label><Input data-testid={`collab-${k}`} value={cForm[k]||""} onChange={(e)=>setCForm({...cForm,[k]:e.target.value})} /></div>
            ))}
            <div><Label>Colore</Label><Input type="color" value={cForm.color||"#C2593F"} onChange={(e)=>setCForm({...cForm,color:e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpenC(false)}>Annulla</Button><Button className="bg-brand text-white" data-testid="collab-save" onClick={saveC}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openT} onOpenChange={setOpenT}>
        <DialogContent data-testid="team-dialog">
          <DialogHeader><DialogTitle className="font-head">Nuova squadra</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input data-testid="team-name" value={tForm.name||""} onChange={(e)=>setTForm({...tForm,name:e.target.value})} /></div>
            <div><Label>Colore</Label><Input type="color" value={tForm.color||"#C2593F"} onChange={(e)=>setTForm({...tForm,color:e.target.value})} /></div>
            <div><Label>Componenti</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {collabs.map((c)=>{const sel=(tForm.member_ids||[]).includes(c.id);return(
                  <button key={c.id} type="button" onClick={()=>toggleMember(c.id)} className={`text-xs px-2.5 py-1 rounded-full border ${sel?"bg-brand text-white border-brand":"bg-white text-muted-foreground"}`}>{c.name}</button>);})}
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpenT(false)}>Annulla</Button><Button className="bg-brand text-white" data-testid="team-save" onClick={saveT}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
